import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import { delete_relationship_by_id } from '../../api/relationshipAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import Switch from 'react-switch'
import Select from 'react-select'
import notify from '../../utils/IANotification';
import { get_user_by_id, create_new_user, update_user, delete_user, disable_user_2fa } from '../../api/usersAPI'
import { fetch_all_roles } from '../../api/roleAPI'
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MdArrowBack, MdSave, MdDelete, MdChurch, MdPerson, MdFamilyRestroom, MdGroups2, MdKey, MdLockOpen } from 'react-icons/md';

export default function MembersFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  let user_data = useFetchUserData()

  const [activeTab, setActiveTab] = useState("personal")

  const [memberFormData, setMemberFormData] = useState({
    first_name: "",
    last_name: "",
    preferred_name: "",
    email: "",
    contact_number: "",
    account_type: "",
    account_remarks: "",
    date_of_birth: "",
    is_church_member: "",
    member_number: "",
    date_joined: "",
    registered_address: "",
    account_role_ids: [],
    serving_history: [],
  })
  const [roleData, setRoleData] = useState([])
  const [password, setPassword] = useState("")
  const [serviceSettings, setServiceSettings] = useState([])

  let user_id = new URLSearchParams(window.location.search).get("user_id")

  const tabs = [
    { id: 'personal', label: 'Personal & Contact' },
    { id: 'church_roles', label: 'Church & Access Roles' },
    { id: 'family_dg', label: 'Family & Disciple Group' },
    { id: 'serving', label: 'Serving History' },
  ]

  const fetchConfigSettings = async () => {
    let endpoint = config.endpoint + "/config"
    let fetchResponse = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await fetchResponse.json()
    if (data.status === 200) {
      let services = data?.data?.services || []
      setServiceSettings(services)
    } else {
      notify({ message: "An error occurred fetching service settings", type: "error" })
    }
  }

  const fetchUserById = async () => {
    if (!user_id || user_id === "new") return
    const data = await get_user_by_id(user_id)
    if (data?.status === 200) {
      setMemberFormData({
        ...data.data,
        account_role_ids: data?.data?.account_role_ids || [],
        serving_history: data?.data?.serving_history || [],
      })
    }
  }

  const fetchAllRoles = async () => {
    let { data, error, message } = await fetch_all_roles()
    if (error) return console.error(message)
    setRoleData(data || [])
  }

  const unlockAccount = async () => {
    let endpoint = config.endpoint + "/auth/account/unlock"
    let response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ user_id: user_id })
    })
    let data = await response.json()
    if (data.status === 200) {
      notify({ message: "Account unlocked successfully", type: "success" })
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const disableUser2FA = async () => {
    if (!user_id) return notify({ message: "Unable to disable 2FA at this time", type: "error" })
    const data = await disable_user_2fa(user_id)
    if (data?.status === 200) {
      notify({ message: "2FA disabled for user", type: "success" })
      fetchUserById()
    } else {
      notify({ message: data?.message || "An error occurred", type: "error" })
    }
  }

  const setUserPassword = async () => {
    if (!password) return notify({ message: "Please enter a new password", type: "error" })
    let endpoint = config.endpoint + "/auth/account/password"
    let response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ user_id: user_id, password })
    })
    let data = await response.json()
    if (data.status === 200) {
      notify({ message: "Password updated successfully", type: "success" })
      setPassword("")
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  useEffect(() => {
    fetchUserById()
    fetchConfigSettings()
    fetchAllRoles()
  }, [])

  const saveUserDetails = async () => {
    if (!user_id || user_id === "new") return notify({ message: "Unable to update user", type: "error" })
    const data = await update_user(user_id, memberFormData)
    if (data?.status === 200) {
      notify({ message: "User details saved successfully", type: "success" })
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const createUser = async () => {
    const data = await create_new_user(memberFormData)
    if (data?.status === 200) {
      notify({ message: "User created successfully", type: "success" })
      navigate("/admin/users")
    } else {
      notify({ message: data.message, type: "error" })
    }
  }

  const deleteUser = async () => {
    if (!user_id || user_id === "new") return
    const data = await delete_user(user_id)
    if (data?.status === 200) {
      notify({ message: "User deleted successfully", type: "success" })
      navigate("/admin/users")
    } else {
      notify({ message: "An error occurred deleting user", type: "error" })
    }
  }

  const deleteRelationship = async (relationship_id) => {
    let data = await delete_relationship_by_id(relationship_id)
    if (data?.status === 200) {
      notify({ message: "Relationship deleted", type: "success" })
      fetchUserById()
    } else {
      notify({ message: "An error occurred", type: "error" })
    }
  }

  return (
    <AdminLayout
      navSelected={"Users"}
      headerText={user_id === "new" ? "New User" : "Edit User Profile"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/users')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {user_id === "new" ? "Create User" : `${memberFormData.first_name} ${memberFormData.last_name}`}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {user_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteUser}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (user_id === "new") createUser()
              else saveUserDetails()
            }}>
              {user_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: PERSONAL & CONTACT */}
      {activeTab === "personal" && (
        <Card title="Personal Information" subtitle="Full name, contact details, date of birth, and address.">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">First Name</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder='e.g. John'
                  value={memberFormData.first_name || ""}
                  onChange={(e) => setMemberFormData({ ...memberFormData, first_name: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Last Name</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder='e.g. Tan'
                  value={memberFormData.last_name || ""}
                  onChange={(e) => setMemberFormData({ ...memberFormData, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Preferred Name</label>
                <input
                  type="text"
                  className="text-input"
                  value={memberFormData.preferred_name || ""}
                  placeholder='e.g. JT'
                  onChange={(e) => setMemberFormData({ ...memberFormData, preferred_name: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Email Address</label>
                <input
                  type="email"
                  className="text-input"
                  value={memberFormData.email || ""}
                  placeholder='e.g. john.tan@gmail.com'
                  onChange={(e) => setMemberFormData({ ...memberFormData, email: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Contact Number</label>
                <input
                  type="tel"
                  className="text-input"
                  value={memberFormData.contact_number || ""}
                  placeholder='e.g. +65 91234567'
                  onChange={(e) => setMemberFormData({ ...memberFormData, contact_number: e.target.value })}
                />
              </div>
            </div>

            <div className="input-container max-w-xs">
              <label className="input-label font-semibold">Date of Birth</label>
              <input
                type="date"
                className="text-input"
                value={memberFormData.date_of_birth?.split("T")[0] || ""}
                onChange={(e) => setMemberFormData({ ...memberFormData, date_of_birth: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Registered Home Address</label>
              <textarea
                className="textarea-input h-24"
                value={memberFormData.registered_address || ""}
                placeholder='e.g. 123 Orchard Road, #01-01, Singapore 123456'
                onChange={(e) => setMemberFormData({ ...memberFormData, registered_address: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: CHURCH & ROLES */}
      {activeTab === "church_roles" && (
        <div className="space-y-6">
          <Card title="Church Membership &amp; Service" subtitle="Service profile and membership information.">
            <div className="space-y-6">
              <div className="input-container">
                <label className="input-label font-semibold">Regular Service Profile</label>
                <Select
                  options={serviceSettings?.map(option => ({ value: option.id, label: option.name }))}
                  value={serviceSettings?.map(option => ({ value: option.id, label: option.name })).find(option => option.value == memberFormData.service_type_id)}
                  onChange={(e) => setMemberFormData({ ...memberFormData, service_type_id: e.value })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Official Church Member</label>
                  <p className="text-xs text-gray-500">Toggle whether this user is an official BMC church member.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {memberFormData.is_church_member == 1 ? "Member" : "Non-Member"}
                  </span>
                  <Switch
                    checked={memberFormData.is_church_member == 1}
                    onChange={(checked) => setMemberFormData({ ...memberFormData, is_church_member: checked ? 1 : 0 })}
                  />
                </div>
              </div>

              {memberFormData?.is_church_member == 1 && (
                <div className="input-container max-w-sm">
                  <label className="input-label font-semibold">Church Membership Number</label>
                  <input
                    type="text"
                    className="text-input"
                    value={memberFormData.member_number || ""}
                    placeholder='e.g. M-12345'
                    onChange={(e) => setMemberFormData({ ...memberFormData, member_number: e.target.value })}
                  />
                </div>
              )}
            </div>
          </Card>

          <Card title="Account Roles &amp; Security" subtitle="Role assignments, password reset, and 2FA settings.">
            <div className="space-y-6">
              <div>
                <label className="input-label font-semibold block mb-3">Assigned System Access Roles</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {roleData?.map((role, index) => {
                    const isChecked = memberFormData?.account_role_ids?.includes(role.id)
                    return (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                        <Switch
                          checked={isChecked}
                          onChange={(checked) => {
                            if (checked) {
                              setMemberFormData({ ...memberFormData, account_role_ids: [...(memberFormData.account_role_ids || []), role.id] });
                            } else {
                              setMemberFormData({ ...memberFormData, account_role_ids: (memberFormData.account_role_ids || []).filter(id => id !== role.id) });
                            }
                          }}
                        />
                        <span className="text-xs font-bold text-gray-900">{role.name}</span>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <label className="input-label font-semibold">Update Account Password</label>
                <div className="flex flex-col sm:flex-row gap-3 mt-1">
                  <input
                    type="password"
                    className="text-input mb-0 flex-grow"
                    placeholder='Enter new account password...'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Button variant="primary" icon={MdKey} onClick={setUserPassword}>
                    Set Password
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <Button variant="outline" icon={MdLockOpen} onClick={unlockAccount}>
                  Unlock Account
                </Button>
                <Button variant="secondary" icon={MdLockOpen} onClick={disableUser2FA}>
                  Disable 2FA
                </Button>
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">Account Remarks &amp; Internal Notes</label>
                <textarea
                  className="textarea-input h-20"
                  value={memberFormData.account_remarks || ""}
                  placeholder="Internal administrative notes..."
                  onChange={(e) => setMemberFormData({ ...memberFormData, account_remarks: e.target.value })}
                />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 3: FAMILY & DG */}
      {activeTab === "family_dg" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Family Connections" subtitle="Registered family members and relational links.">
            {memberFormData?.relationship?.length > 0 ? (
              <div className="space-y-3">
                {memberFormData?.relationship?.map((relationship, index) => {
                  let relation = ""
                  let user = {}
                  if (relationship.relationship_type === "parent") {
                    if (relationship.parent_user_id == user_data?.id) {
                      relation = "Child"
                      user = relationship?.user_2_data
                    } else {
                      relation = "Parent"
                      user = relationship?.user_1_data
                    }
                  } else if (relationship.relationship_type === "guardian") {
                    if (relationship.parent_user_id == user_data?.id) {
                      relation = "Ward"
                      user = relationship?.user_2_data
                    } else {
                      relation = "Guardian"
                      user = relationship?.user_1_data
                    }
                  } else if (relationship.relationship_type === "spouse") {
                    relation = "Spouse"
                    user = relationship.parent_user_id == user_data?.id ? relationship?.user_2_data : relationship?.user_1_data
                  } else if (relationship.relationship_type === "sibling") {
                    relation = "Sibling"
                    user = relationship.parent_user_id == user_data?.id ? relationship?.user_2_data : relationship?.user_1_data
                  }

                  return (
                    <div key={index} className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex items-center gap-3">
                        <MdFamilyRestroom size={20} className="text-blue-500" />
                        <div>
                          <p className='text-xs font-bold text-gray-900'>{user?.first_name} {user?.last_name}</p>
                          <p className='text-[10px] font-semibold text-gray-500 uppercase tracking-wider'>{relation}</p>
                        </div>
                      </div>
                      <Button variant="danger" size="sm" icon={MdDelete} onClick={() => deleteRelationship(relationship.id)}>
                        Remove
                      </Button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic py-4">No family relationships configured yet.</p>
            )}
          </Card>

          <Card title="Disciple Group (DG)" subtitle="Current disciple group membership and assignment.">
            {memberFormData?.dg?.id ? (
              <div className="space-y-4">
                {memberFormData?.dg?.file_data?.ext_file_path && (
                  <img
                    src={`${config.endpoint}${memberFormData?.dg?.file_data?.ext_file_path}`}
                    alt=""
                    className='w-full h-36 object-cover rounded-xl border border-gray-200'
                  />
                )}
                <div>
                  <h4 className="font-bold text-base text-gray-900">{memberFormData?.dg?.name}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{memberFormData?.dg?.tagline || "Disciple Group"}</p>
                </div>
                <Button variant="primary" size="sm" icon={MdGroups2} onClick={() => navigate("/admin/dg/details?dg_id=" + memberFormData?.dg?.id)}>
                  View DG Profile
                </Button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic py-4">User is not currently assigned to a Disciple Group.</p>
            )}
          </Card>
        </div>
      )}

      {/* TAB 4: SERVING HISTORY / PLACES SERVED */}
      {activeTab === "serving" && (
        <Card title="Serving History &amp; Ministry Roles" subtitle="Comprehensive record of all ministry and service teams this user has served in.">
          {memberFormData?.serving_history?.length > 0 ? (
            <div className="space-y-3">
              {memberFormData?.serving_history?.map((server, index) => {
                const isCurrent = server.is_current === "1" || server.is_current === 1 || server.is_current === true
                return (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${isCurrent ? "bg-emerald-100 text-emerald-600" : "bg-gray-200 text-gray-500"}`}>
                        <MdChurch size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-gray-900">{server.ministry_name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isCurrent
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-gray-200 text-gray-700"
                            }`}>
                            {isCurrent ? "Active Server" : "Past Server"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 font-semibold mt-0.5">Role: {server.role || "Team Member"}</p>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 font-medium">
                      {server.start_date || "N/A"} &mdash; {server.end_date || (isCurrent ? "Present" : "Ended")}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400 space-y-2">
              <MdChurch size={40} className="mx-auto text-gray-300" />
              <p className="text-xs font-medium">No serving history recorded for this user yet.</p>
            </div>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
